import type { SortOrderType } from '@atlaskit/dynamic-table/types';

import type { DirectoryType, PlatformRole, UserProductRole } from '@adminhub/graphql-types';

import type { OperationalEvent, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, OperationalAction, OperationalSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

const unifiedUserProfileScreen = 'unifiedUserProfileScreen';
const unifiedUserListScreen = 'unifiedUserListScreen';

export type UULSource = typeof unifiedUserListScreen;
export type UUPSource = typeof unifiedUserProfileScreen;

export interface VortexSiteAdminAttributes {
  vortexStatus: boolean;
  tenantCount?: number;
  enhancedSiteAdmin: boolean;
  userRole?: string;
}

export const unifiedUserListEvents = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/4322133136/UUL+Analytic+Specs+M1',
  screen: {
    unifiedUserListScreen: (attributes: {
      totalUsers: number;
      managedUsers: number;
      orgAdmins: number;
      orgType?: DirectoryType;
      isOrgAdmin?: boolean;
      isSiteAdmin?: boolean;
      numberOfSites?: number;
      currentOrgSiteAdminCount?: number;
      pendingRaSiteAdminMultiSiteExperimentCohort?: string;
      showPendingRAButtonNonVortexCohort?: string;
      currentOrgDirectoryCount?: number;
    }): ScreenEvent => ({
      data: {
        name: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75042',
    }),
  },
  ui: {
    openInviteUserDrawerButtonClicked: (attributes: { totalUserCount?: number | null; userCountOnScreen?: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'openInviteUserDrawer',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14633',
    }),
    exportButtonClicked: (attributes: { totalUserCount?: number | null; userCountOnScreen?: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'exportUsers',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14634',
    }),
    sortButtonClicked: (attributes: { sortKey: string; sortDirection: SortOrderType }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'sortByColumn',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74647',
    }),
    lastActiveToolTipHovered: (): UIEvent => ({
      data: {
        action: Action.Hovered,
        actionSubject: ActionSubject.Tooltip,
        actionSubjectId: 'lastActiveToolTip',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74750',
    }),
    statusToolTipHovered: (attributes: { status: string }): UIEvent => ({
      data: {
        action: Action.Hovered,
        actionSubject: ActionSubject.Tooltip,
        actionSubjectId: 'statusToolTip',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74754',
    }),
    resultsPerPageButtonClicked: (attributes: { resultsPerPage: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'resultsPerPage',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74515',
    }),
    changePageButtonClicked: (attributes: { direction: string; previousResultsRange: string; previousPageNumber: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'changePage',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74755',
    }),
    resetFiltersButtonClicked: (attributes: { source?: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'resetFilters',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75011',
    }),
    userTablefilteredEmptyViewShown: (): UIEvent => ({
      data: {
        action: Action.Shown,
        actionSubject: ActionSubject.EmptyTable,
        actionSubjectId: 'noUsersScreen',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74825',
    }),
    userListFilterRoleChanged: (attributes: { roleFilterOption: (PlatformRole | UserProductRole)[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'roleFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74928',
    }),
    userListFilterStatusChanged: (attributes: { statusFilterOption }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'statusFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74986',
    }),
    userListFilterProductAccessChanged: (attributes: { productAccessFilterOption: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'productAccessFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74988',
    }),
    userListFilterAccountTypeChanged: (attributes: { accountTypeOptions: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'accountTypeFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/78851',
    }),
    userListFilterDomainChanged: (attributes: { domainOptions: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'domainFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/78852',
    }),
    userListFilterDirectoryChanged: (attributes: { directoryFilterOptions: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'directoryFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/88952',
    }),
    userListFilterEmailVerifiedChanged: (attributes: { emailVerifiedFilterOption: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'emailVerificationFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92997',
    }),
    twoStepVerificationFilterChanged: (attributes: { twoStepVerificationFilterOption: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'twoStepVerificationFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92999',
    }),
    sourceFilterChanged: (attributes: { sourceFilterOption: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Option,
        actionSubjectId: 'sourceFilter',
        source: unifiedUserListScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93002',
    }),
  },
  track: {
    userListExportDownloaded: (): TrackEvent => ({
      data: {
        action: TrackAction.UserlistDownloaded,
        actionSubject: TrackActionSubject.Export,
        actionSubjectId: 'exportUsersActions',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75163',
    }),
    managedAccountsExportDownloaded: (): TrackEvent => ({
      data: {
        action: TrackAction.ManagedAccountsDownloaded,
        actionSubject: TrackActionSubject.Export,
        actionSubjectId: 'exportManagedAccounts',
        source: unifiedUserListScreen,
      },
    }),
  },
  operational: {
    usersLastActiveDataFetchError: (): OperationalEvent => ({
      data: {
        action: OperationalAction.Error,
        actionSubject: OperationalSubject.UsersLastActiveDataFetch,
        actionSubjectId: 'lastActiveColumn',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75347',
    }),
    userListLastActiveSuccess: (): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: OperationalSubject.UsersLastActiveDataFetch,
        actionSubjectId: 'lastActiveSuccess',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75401',
    }),
    usersStatsDataFetchError: (): OperationalEvent => ({
      data: {
        action: OperationalAction.Error,
        actionSubject: OperationalSubject.UsersStatsDataFetch,
        actionSubjectId: 'usersSummaryBlocks',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75345',
    }),
    usersStatsDataFetchTaskSuccess: (): OperationalEvent => ({
      data: {
        action: OperationalAction.TaskSuccess,
        actionSubject: OperationalSubject.UsersStatsDataFetch,
        actionSubjectId: 'usersSummaryBlocks',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75346',
    }),
    userListTableSearchTaskStart: (): OperationalEvent => ({
      data: {
        action: OperationalAction.Started,
        actionSubject: OperationalSubject.GraphQL,
        actionSubjectId: 'search userListTable taskStart',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75405',
    }),
    userListTableSearchTaskSuccess: (): OperationalEvent => ({
      data: {
        action: OperationalAction.TaskSuccess,
        actionSubject: OperationalSubject.GraphQL,
        actionSubjectId: 'search userListTable taskSuccess',
        source: unifiedUserListScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/75406',
    }),
  },
};
